import { Box, Stack } from '@mui/material';
import { useEffect } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { ApplicationConfigurationFormShape } from '@/pages/admin/ApplicationConfigurationForm/ApplicationConfigurationFormFields';
import { PresentationReviewField } from '@/pages/admin/ApplicationConfigurationForm/PresentationReviewField';
import { PresentationApprovalRequirement } from '@/types/schema';

import {
  useTenantConfigurationFormQuery,
  useUpdateTenantConfigurationFormMutation,
} from './graphql/TenantConfigurationForm.generated';

type TenantPresentationSettingsFormShape = Pick<
  ApplicationConfigurationFormShape,
  'requirePresentationApproval'
>;

export function TenantPresentationSettingsForm() {
  const formMethods = useForm<TenantPresentationSettingsFormShape>({
    defaultValues: {
      requirePresentationApproval: PresentationApprovalRequirement.NotRequired,
    },
  });
  const { handleSubmit, reset } = formMethods;
  const { createSuccessFeedback, showFeedback } = useFeedback();

  // TenantConfigurationForm runs the same query and shows the error feedback
  // if it fails, so this form does not show it again.
  const { data } = useTenantConfigurationFormQuery();
  const config = data?.tenantApplicationConfiguration;

  useEffect(() => {
    if (!config) return;
    reset({ requirePresentationApproval: config.requirePresentationApproval });
  }, [config, reset]);

  const [updateTenantConfiguration, { loading }] =
    useUpdateTenantConfigurationFormMutation({
      onCompleted: createSuccessFeedback(
        'Successfully updated presentation settings'
      ),
      onError: (error) => {
        const userFacingMessages = getUserFacingErrorMessages(error);
        if (userFacingMessages.length > 0) {
          showFeedback(userFacingMessages.join('. '));
        } else {
          showFeedback(
            'There was an error updating the presentation settings. Please try again.'
          );
        }
      },
    });

  const onSubmit = handleSubmit((formValues) => {
    if (!config) {
      throw new Error('No tenant configuration id found');
    }

    // Send only the fields in this card, so the update does not change the
    // values in the tenant configuration card.
    return updateTenantConfiguration({
      variables: {
        input: {
          id: config.id,
          update: {
            requirePresentationApproval: formValues.requirePresentationApproval,
          },
        },
      },
    });
  });

  return (
    <FormProvider {...formMethods}>
      <Stack component="form" spacing={2} maxWidth={400} onSubmit={onSubmit}>
        <PresentationReviewField />
        <Box>
          <Button loading={loading} variant="primary" type="submit" size="sm">
            Save updates
          </Button>
        </Box>
      </Stack>
    </FormProvider>
  );
}
